import type { iEntityValueFilterConfig } from "@mitratech/north-star";
import {
  ButtonVariant,
  ChipVariant,
  ColorVariant,
  IconName,
  SizeVariant,
  eAnchorTarget,
  eAsidePanelType,
  eCTA,
  iAsideEntityPanelSpec,
  iAsidePanelBase,
  tAsideNavPanelSpec,
} from "@mitratech/north-star";

import { isQuickAssessmentEnabled } from "../config/featureFlags";
import { INTEGRATIONS_API_PANEL_ID } from "./integrationsApi";
import { caseContextMenuItemsFor } from "../features/cases/constants";
import { Case, CaseStatus } from "../types/loa";

const DEFAULT_CREATE_CLIENT_ROUTE_SLUG = "abdfasdfdfjhdfjhdf";
const RESERVED_CREATE_CLIENT_ROUTE_SLUGS = new Set([
  "company-details",
  "company-hub",
  "employees",
  "integrations",
  "policies-and-documents",
  "users",
]);

function normalizeCreateClientRouteSlug(value: string | undefined): string {
  const slug = value?.trim().replace(/^\/+|\/+$/g, "");

  if (!slug) {
    return DEFAULT_CREATE_CLIENT_ROUTE_SLUG;
  }

  if (slug.includes("/") || RESERVED_CREATE_CLIENT_ROUTE_SLUGS.has(slug)) {
    return DEFAULT_CREATE_CLIENT_ROUTE_SLUG;
  }

  return slug;
}

// Runtime value (from the ECS task definition's plain env var, injected by
// docker/entrypoint.d/50-inject-runtime-env.sh) wins when present, since it's
// the only mechanism deployed environments actually use — the build-time
// import.meta.env value only ever applies to native/local Vite dev, via
// .env.local. `||` (not `??`) deliberately: the entrypoint script always
// writes a string, so an unset task-def var comes through as "", which must
// fall through to the build-time value same as undefined would.
export const CREATE_CLIENT_ROUTE_SLUG = normalizeCreateClientRouteSlug(
  window.__RUNTIME_ENV__?.VITE_CREATE_CLIENT_ROUTE_SLUG || import.meta.env.VITE_CREATE_CLIENT_ROUTE_SLUG,
);

const dashboardPanel: tAsideNavPanelSpec = {
  id: "dashboard",
  type: eAsidePanelType.Navigation,
  icon: IconName.HomeFilled,
  title: "Home",
  tooltip: "Home",
  config: {
    items: [
      {
        id: "home",
        text: "Home",
        label: "See Leave of Absense Home",
        tooltip: "See Leave of Absense Home",
        icon: IconName.HomeFilled,
        href: "/dashboard",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "action-items",
        cta: "View Action Items",
        label: "View and manage case action items across open leave cases.",
        tooltip: "Action Items",
        text: "Action Items",
        icon: IconName.CheckCircleOutline,
        href: "/action-items",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "create-case",
        text: "Cases",
        cta: "Open Cases",
        // The accessible name must contain the visible one ("Cases").
        label: "Cases. Create a case using the ARIES chat.",
        tooltip: "Cases",
        icon: IconName.FolderOpen,
        href: "/cases",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "settings",
        cta: "Change Password",
        label: "Update your account password.",
        tooltip: "Change Password",
        text: "Change Password",
        icon: IconName.ManageAccounts,
        href: "/settings",
        target: eAnchorTarget.Self,
        display: import.meta.env.VITE_LOCAL_AUTH === "true",
      },
      {
        id: "company-details",
        text: "Company Details",
        cta: "Edit Company Details",
        label: "Manage and edit your company's information, settings, and preferences.",
        tooltip: "See Company Details",
        icon: IconName.Apartment,
        href: "/dashboard/company-details",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "policies-and-documents",
        text: "Policies and Documents",
        cta: "Manage Policies & Documents",
        label: "Add, edit, and manage your company's leave policies and benefits.",
        tooltip: "Browse Company's Policies and Documents",
        icon: IconName.TextSnippet,
        href: "/dashboard/policies-and-documents",
        target: eAnchorTarget.Self,
        display: true,
        disabled: true,
        tags: [
          {
            id: "coming-soon",
            label: "Coming Soon",
            text: "Coming Soon",
            variant: ChipVariant.Contained,
            color: ColorVariant.Secondary,
          },
        ],
      },
      {
        id: "employees",
        text: "Employees",
        cta: "Manage Employees",
        label: "Add, edit, and manage employee accounts, including updating details, roles, and permissions.",
        tooltip: "List of Employees",
        icon: IconName.ManageAccounts,
        href: "/dashboard/employees",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "integrations",
        cta: "Integration Settings",
        label: "Connect and manage integrations with external systems, such as HRIS platforms.",
        tooltip: "See and Manage Integrations",
        text: "Integrations",
        icon: IconName.NetworkNode,
        href: "/dashboard/integrations",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "assessment",
        cta: "Open Assessment",
        label: "Chat with the Leave assessment agent about your organization.",
        tooltip: "Open the Assessment",
        text: "Assessment",
        icon: IconName.Chat,
        href: "/assessment",
        target: eAnchorTarget.Self,
        display: isQuickAssessmentEnabled(),
      },
      {
        id: "users",
        cta: "Assign Leave Users",
        label: "Assign users to access the Leave platform and create leave of absence requests.",
        tooltip: "User Management",
        text: "Users",
        icon: IconName.Group,
        href: "/dashboard/users",
        target: eAnchorTarget.Self,
        display: true,
      },
    ],
  },
};

const casesPanel: iAsidePanelBase<eAsidePanelType.Entity, iAsideEntityPanelSpec<Case>> = {
  id: "cases",
  type: eAsidePanelType.Entity,
  icon: IconName.FolderOpen,
  title: "Cases",
  tooltip: "Cases",
  // No rail icon: Home's Cases item opens it, and it shows while a case is open.
  showInNavStrip: false,
  cta: {
    type: eCTA.Button,
    display: true,
    config: {
      id: "createCase",
      name: "createCase",
      label: "Create a new Leave Case",
      tooltip: "Create Leave Case",
      tooltipPlacement: "left",
      color: ColorVariant.Primary,
      variant: ButtonVariant.Contained,
      size: SizeVariant.Medium,
      icon: IconName.Add,
      disabled: false,
    },
  },
  config: {
    icon: IconName.FolderShared,
    entityNameSingular: "Case",
    entityNamePlural: "Cases",
    isFilterable: true,
    filterBy: [
      "id",
      "case_identifier",
      "employee.id",
      "employee.first_name",
      "employee.last_name",
      "employee.work_email",
      "employee.employee_id",
      "leave_reason.name",
      "leaveType",
      "status",
    ],
    uIdAttribute: "id",
    // Row navigation/keys/context-menu still key off the UUID (`id`) above —
    // this only swaps the displayed `#…` text for the human-readable id.
    displayIdAttribute: "case_identifier",
    textAttribute: "employee",
    subtextAttribute: "full_name",
    prefixIconField: "status",

    // Sidebar filter values are `string[]` end-to-end (URL state, EntityPanel
    // checkbox state, and `filterItemsByKnownValue` which compares via string
    // coercion). Stringify the numeric CaseStatus enum here so the value flows
    // through that pipeline cleanly.
    valueFilter: {
      field: "status",
      tooltip: "Case Status Filter",
      options: [
        {
          id: "f-approved",
          label: "Approved",
          value: String(CaseStatus.Approved),
          prefix: IconName.StatusActive,
          color: ColorVariant.Success,
        },
        {
          id: "f-open",
          label: "Open",
          value: String(CaseStatus.Open),
          prefix: IconName.StatusOpen,
          color: ColorVariant.Primary,
        },
        {
          id: "f-closed",
          label: "Closed",
          value: String(CaseStatus.Closed),
          prefix: IconName.StatusClosed,
          color: ColorVariant.Secondary,
        },
        {
          id: "f-cancelled",
          label: "Cancelled",
          value: String(CaseStatus.Cancelled),
          prefix: IconName.StatusPending,
          color: ColorVariant.Warning,
        },
        {
          id: "f-denied",
          label: "Denied",
          value: String(CaseStatus.Denied),
          prefix: IconName.StatusDenied,
          color: ColorVariant.Error,
        },
      ],
    } satisfies iEntityValueFilterConfig<{ status: CaseStatus }>,

    contextMenuFor: (entity: Case) => {
      return {
        items: caseContextMenuItemsFor(entity.status),
        placement: "bottom-end" as const,
      };
    },
  },
};

const actionItemsPanel: tAsideNavPanelSpec = {
  id: "action-items",
  type: eAsidePanelType.Navigation,
  icon: IconName.CheckCircleOutline,
  title: "Action Items",
  tooltip: "Action Items",
  // No rail icon: Home's Action Items item opens it.
  showInNavStrip: false,
  config: {
    items: [
      // "My" comes first and owns the bare path: the worklist defaults to the
      // signed-in user's items, so `/action-items` and this entry are the same
      // view. "All" has to say so explicitly.
      {
        id: "action-items-my",
        text: "My Action Items",
        cta: "View My Action Items",
        label: "Case action items assigned to you.",
        tooltip: "My Action Items",
        icon: IconName.AccountCircle,
        href: "/action-items",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "action-items-all",
        text: "All Action Items",
        cta: "View All Action Items",
        label: "View every case action item across cases you can access.",
        tooltip: "All Action Items",
        icon: IconName.CheckCircleOutline,
        href: "/action-items?scope=all",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "action-items-overdue",
        text: "Overdue Action Items",
        cta: "View Overdue Action Items",
        label: "Case action items past their due date.",
        tooltip: "Overdue Action Items",
        icon: IconName.WarningOutline,
        href: "/action-items/overdue",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "action-items-unassigned",
        text: "Unassigned Action Items",
        cta: "View Unassigned Action Items",
        label: "Case action items nobody has picked up yet.",
        tooltip: "Unassigned Action Items",
        icon: IconName.RemoveCircleOutline,
        href: "/action-items/unassigned",
        target: eAnchorTarget.Self,
        display: true,
      },
    ],
  },
};

/**
 * Super Admin Settings: feature toggles, integration keys and the API routes
 * (LOA-1473). Feature toggles come first and are where its rail icon lands. Not part
 * of `panelData`: it is added by `usePanelConfig` only for platform operators, so
 * everyone else never sees the icon. The panel id stays `integrations-api` (it is
 * the rail key and the route prefix); only the name people see changed.
 */
export const integrationsApiPanel: tAsideNavPanelSpec = {
  id: INTEGRATIONS_API_PANEL_ID,
  type: eAsidePanelType.Navigation,
  icon: IconName.NetworkNode,
  title: "Super Admin Settings",
  tooltip: "Super Admin Settings",
  config: {
    items: [
      {
        id: "feature-toggles",
        text: "Feature Toggles",
        cta: "Manage Feature Toggles",
        label: "Turn new features, such as the agent integration, on for a tenant.",
        tooltip: "Feature Toggles",
        icon: IconName.Power,
        href: "/settings/feature-toggles",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "integration-keys",
        text: "Integration Keys",
        cta: "Manage Integration Keys",
        label: "Create, rotate and revoke the keys the Quick agent uses.",
        tooltip: "Integration Keys",
        icon: IconName.Lock,
        href: "/settings/integration-keys",
        target: eAnchorTarget.Self,
        display: true,
      },
      {
        id: "integration-routes",
        text: "API Routes",
        cta: "View API Routes",
        label: "The integration endpoints, with their URLs.",
        tooltip: "API Routes",
        icon: IconName.Link,
        href: "/settings/integration-routes",
        target: eAnchorTarget.Self,
        display: true,
      },
    ],
  },
};

// NOTE: rail-icon clicks navigate to `/<key>/` (see ProtectedLayout
// handlePanelClick), so each key here must match a real top-level route.
export const panelData = {
  dashboard: dashboardPanel,
  cases: casesPanel,
  "action-items": actionItemsPanel,
};
